import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { createLogger } from '~/utils/logging';

const log = createLogger('setupStore');

export type VoiceNameItem = {
  name: string; // committed valid name
  draft: string; // current editing value (may be invalid)
  validationState: 'valid' | string; // "valid" or error message
};

export const REFERRAL_SOURCES = [
  { label: 'My school is using Socrait', value: 'school', telemetryLabel: 'referral-source-school' },
  { label: 'TikTok', value: 'tiktok', telemetryLabel: 'referral-source-tiktok' },
  { label: 'Facebook', value: 'facebook', telemetryLabel: 'referral-source-facebook' },
  { label: 'Instagram', value: 'instagram', telemetryLabel: 'referral-source-instagram' },
  { label: 'YouTube', value: 'youtube', telemetryLabel: 'referral-source-youtube' },
  { label: 'Conference', value: 'conference', telemetryLabel: 'referral-source-conference' },
  { label: 'Google', value: 'google', telemetryLabel: 'referral-source-google' },
  { label: 'Word of Mouth', value: 'wordOfMouth', telemetryLabel: 'referral-source-word-of-mouth' },
  { label: 'Email', value: 'email', telemetryLabel: 'referral-source-email' },
  { label: 'Other', value: 'other', telemetryLabel: 'referral-source-other' },
];

export const LOCATION_OPTIONS = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'Other',
];

export interface SetupStore {
  // Teacher Setup Step
  referredToName: string;
  setReferredToName: (name: string) => void;
  schoolName: string;
  setSchoolName: (schoolName: string) => void;
  teachingStyle: 'single' | 'multiple' | 'unknown';
  setTeachingStyle: (teachingStyle: 'single' | 'multiple') => void;
  location: string;
  setLocation: (location: string) => void;
  tosAgreedAt: string;
  setTosAgreedAt: (timestamp: string) => void;
  isTeacherDataValid: () => boolean;

  // Referral Source Step
  referralSource: string;
  setReferralSource: (source: string) => void;
  isReferralSourceValid: () => boolean;

  // Class Setup Step
  firstClassName: string;
  setFirstClassName: (className: string) => void;
  firstShortName: string;
  setFirstShortName: (firstShortName: string) => void;
  isClassDataValid: () => boolean;

  // Roster Setup Step
  addNameInputValue: string;
  setAddNameInputValue: (value: string) => void;
  isEditingVoiceName: boolean;
  setIsEditingVoiceName: (isEditing: boolean) => void;
  voiceNameItems: VoiceNameItem[];
  setVoiceNameItems: (items: VoiceNameItem[]) => void;
  isRosterValid: () => boolean;

  // Global Utilities
  clear: () => void;
}

export const setupStore = create<SetupStore>()(
  persist(
    (set, get) => ({
      // Teacher Setup Step
      referredToName: '',
      setReferredToName: (name) => {
        set({ referredToName: name });
      },
      schoolName: '',
      setSchoolName: (schoolName) => {
        set({ schoolName: schoolName });
      },
      teachingStyle: 'unknown',
      setTeachingStyle: (teachingStyle) => {
        set({ teachingStyle: teachingStyle });
      },
      location: '',
      setLocation: (location) => {
        set({ location: location });
      },
      tosAgreedAt: '',
      setTosAgreedAt: (timestamp) => {
        set({ tosAgreedAt: timestamp });
      },
      isTeacherDataValid: () => {
        const state = get();
        if (!state.referredToName.trim()) {
          log.debug('Teacher data validation error: referredTo name is empty');
          return false;
        }

        if (!state.schoolName.trim()) {
          log.debug('Teacher data validation error: school name is empty');
          return false;
        }

        if (!state.location.trim()) {
          log.debug('Teacher data validation error: location is empty');
          return false;
        }

        if (state.teachingStyle === 'unknown') {
          log.debug('Teacher data validation error: teaching style is not selected');
          return false;
        }

        if (!state.tosAgreedAt) {
          log.debug('Teacher data validation error: terms of service not agreed to');
          return false;
        }

        log.debug('Teacher data validation result: valid');
        return true;
      },
      // Referral Source Step
      referralSource: '',
      setReferralSource: (source) => {
        set({ referralSource: source });
      },
      isReferralSourceValid: () => {
        const state = get();
        return state.referralSource !== '';
      },
      // Class Setup Step
      firstClassName: '',
      setFirstClassName: (className) => {
        set({ firstClassName: className });
      },
      firstShortName: '',
      setFirstShortName: (firstShortName) => {
        set({ firstShortName: firstShortName });
      },
      isClassDataValid: () => {
        const state = get();
        if (!state.firstClassName.trim()) {
          log.debug('Class data validation error: class name is empty');
          return false;
        }

        if (!state.firstShortName.trim()) {
          log.debug('Class data validation error: short name is empty');
          return false;
        }

        return true;
      },
      // Roster Setup Step
      addNameInputValue: '',
      setAddNameInputValue: (value) => {
        set({ addNameInputValue: value });
      },
      isEditingVoiceName: false,
      setIsEditingVoiceName: (isEditing) => {
        set({ isEditingVoiceName: isEditing });
      },
      voiceNameItems: [],
      setVoiceNameItems: (items) => {
        set({ voiceNameItems: items });
      },
      isRosterValid: () => {
        const state = get();

        if (state.isEditingVoiceName) {
          log.debug('Class data validation error: still editing voice names');
          return false;
        }

        if (state.voiceNameItems.length === 0) {
          log.debug('Class data validation error: no voice names');
          return false;
        }

        // Check if all items have valid validation state and that there are no uncommitted drafts
        const allValid = state.voiceNameItems.every(
          (item) => item.validationState === 'valid' && item.draft === item.name,
        );
        if (!allValid) {
          log.debug('Class data validation error: some items have validation errors');
          return false;
        }

        log.debug('Class data validation result: valid');
        return true;
      },
      // Global Utilities
      clear: () => {
        set({
          referredToName: '',
          schoolName: '',
          teachingStyle: 'unknown',
          location: '',
          tosAgreedAt: '',
          referralSource: '',
          firstClassName: '',
          firstShortName: '',
          voiceNameItems: [],
          isEditingVoiceName: false,
          addNameInputValue: '',
        });
      },
    }),
    {
      name: 'setupStorage',
    },
  ),
);
